import { useNavigate } from '@tanstack/react-router';
import { useCTUser } from '@ct/oneid/react';
import { Badge, Button, Separator } from '@vta/ui';
import type { Program } from '@vta/types';
import { useFilterStore } from '../../stores/filter-store.ts';
import { useCommandPaletteStore } from '../../stores/command-palette-store.ts';
import { redirectToHostedSignIn } from '../../lib/auth.ts';
import { HeaderSearchTrigger } from '../../features/search/HeaderSearchTrigger.tsx';
import { GlobeIcon } from '../icons.tsx';
import { UserMenu } from './UserMenu.tsx';

/**
 * Top app bar (PRD §9.2 desktop).
 *
 * Layout:
 *   [logo]   [search trigger]   [program filter]   [event selector]   [auth]
 *
 * The actual cmdk command palette is owned by Chunk 9 (SEARCH-001). Until
 * then the inline search input is a focus-only stub that focuses the
 * router's root search field via Cmd/Ctrl+K wire-up. This shell guarantees
 * the visual real estate, keyboard contract, and a11y landmarks are stable
 * so the Search agent can drop in cmdk without layout churn.
 */
// Only real programs surface as filter chips; the `unknown` enum value is a
// data-quality bucket from sync, never user-selectable.
type FilterableProgram = Exclude<Program, 'unknown'>;
const PROGRAM_OPTIONS: ReadonlyArray<{ value: FilterableProgram; label: string }> = [
  { value: 'v5rc', label: 'V5RC' },
  { value: 'viqrc', label: 'VIQRC' },
  { value: 'vurc', label: 'VURC' },
  { value: 'vairc', label: 'VAIRC' },
];

export function HeaderBar({ floating = false }: { floating?: boolean }) {
  const navigate = useNavigate();
  const program = useFilterStore((s) => s.program);
  const event = useFilterStore((s) => s.event);
  const openPalette = useCommandPaletteStore((s) => s.setOpen);
  const { user, isLoading } = useCTUser();

  const setProgram = (value: Program | undefined) => {
    // `from: '/'` scopes the search-reducer types to the root validateSearch
    // schema. Without it, the reducer is typed against the intersection of
    // every route's search schema, which can collapse to `never`.
    navigate({
      to: '.',
      from: '/',
      search: (prev: Record<string, unknown>) => ({ ...prev, program: value }),
      replace: true,
    });
  };

  return (
    <header
      className={
        floating
          ? // Atlas mode: float over the globe on a gradient scrim, no border —
            // the canvas runs edge to edge behind it.
            'absolute inset-x-0 top-0 z-sticky flex h-14 items-center gap-3 bg-gradient-to-b from-bg-sunken/85 via-bg-sunken/40 to-transparent px-4 md:h-16 md:px-6'
          : 'sticky top-0 z-sticky flex h-14 shrink-0 items-center gap-3 border-b border-border-subtle bg-bg-base/85 px-4 backdrop-blur-md md:h-16 md:px-6'
      }
      role="banner"
    >
      {/* Wordmark — minimal, mono kicker + display lockup. */}
      <a
        href="/"
        className="flex items-center gap-2 text-fg transition-colors duration-quick hover:text-accent"
        aria-label="VEX Teams Atlas — home"
      >
        <GlobeIcon className="h-5 w-5 text-accent" />
        <span className="hidden font-display text-sm tracking-tight md:inline">
          VEX Teams Atlas
        </span>
        <span className="font-mono text-2xs uppercase tracking-[0.3em] text-fg-subtle md:hidden">
          VTA
        </span>
      </a>

      <Separator orientation="vertical" className="hidden h-6 md:block" />

      {/* Real cmdk overlay; trigger button opens it via the shared store. */}
      <HeaderSearchTrigger onClick={() => openPalette(true)} className="flex-1 md:flex-none" />

      {/* Program filter — multi-state chip group. Hidden on the smallest
          breakpoints to prioritize the search input. */}
      <nav
        aria-label="Program filter"
        className="hidden items-center gap-1 lg:flex"
      >
        {PROGRAM_OPTIONS.map((opt) => {
          const active = program === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => setProgram(active ? undefined : opt.value)}
              className="text-2xs font-medium uppercase tracking-[0.2em] text-fg-muted transition-colors duration-quick hover:text-fg aria-pressed:text-fg"
            >
              <Badge variant={active ? opt.value : 'neutral'}>{opt.label}</Badge>
            </button>
          );
        })}
      </nav>

      {/* Event selector — placeholder; real selector arrives with SEARCH-002.
          We render the active event badge if URL state already has one so the
          shell never looks broken when a user lands on a deep-linked URL. */}
      {event ? (
        <Badge variant="accent" className="hidden sm:inline-flex">
          Event · {event}
        </Badge>
      ) : null}

      <div className="ml-auto flex items-center gap-2">
        {isLoading ? null : user ? (
          <UserMenu />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => redirectToHostedSignIn(window.location.href)}
          >
            Sign in
          </Button>
        )}
      </div>
    </header>
  );
}
